import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, Clock, GraduationCap, Monitor, ChevronDown, ExternalLink } from 'lucide-react';
import { SiFacebook, SiX, SiLinkedin, SiInstagram } from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [portalsOpen, setPortalsOpen] = useState(false);
  const portalsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (portalsRef.current && !portalsRef.current.contains(e.target as Node)) {
        setPortalsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigationItems = [
    { label: 'الرئيسية', path: ROUTE_PATHS.HOME },
    { label: 'الأقسام', path: ROUTE_PATHS.DEPARTMENTS },
    { label: 'القبول', path: ROUTE_PATHS.ADMISSIONS },
    { label: 'هيئة التدريس', path: ROUTE_PATHS.FACULTY },
    { label: 'الأخبار', path: ROUTE_PATHS.NEWS },
    { label: 'الاعتمادات', path: ROUTE_PATHS.ACCREDITATIONS },
    { label: 'اللوائح', path: ROUTE_PATHS.REGULATIONS },
    { label: 'اتصل بنا', path: ROUTE_PATHS.CONTACT },
  ];

  const portalLinks = [
    {
      label: 'بوابة الطالب',
      url: 'https://xlserver.al-emtiaz.net//XLWeb/Default.aspx?u=ColTechGateLibya',
      icon: GraduationCap,
      desc: 'نتائج، جداول، بيانات الطالب',
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: 'منصة التعلم الإلكتروني',
      url: 'https://cgot.edu.ly/moodle44',
      icon: Monitor,
      desc: 'المحاضرات، الواجبات، الاختبارات',
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
  ];

  const quickLinks = [
    { label: 'الرئيسية', path: ROUTE_PATHS.HOME },
    { label: 'الأقسام الأكاديمية', path: ROUTE_PATHS.DEPARTMENTS },
    { label: 'القبول والتسجيل', path: ROUTE_PATHS.ADMISSIONS },
    { label: 'الأخبار والفعاليات', path: ROUTE_PATHS.NEWS },
    { label: 'الاعتمادات والتراخيص', path: ROUTE_PATHS.ACCREDITATIONS },
    { label: 'اللوائح الأكاديمية', path: ROUTE_PATHS.REGULATIONS },
    { label: 'اتصل بنا', path: ROUTE_PATHS.CONTACT },
  ];

  const academicServices = [
    { label: 'بوابة الطالب', url: 'https://xlserver.al-emtiaz.net//XLWeb/Default.aspx?u=ColTechGateLibya' },
    { label: 'منصة التعلم الإلكتروني', url: 'https://cgot.edu.ly/moodle44' },
    { label: 'المكتبة الرقمية', url: '#' },
    { label: 'البحث العلمي', url: '#' },
  ];

  const handleNavClick = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen flex flex-col" dir="rtl">
      {/* Top Bar removed as requested */}

      {/* Main Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3 group">
              <div className="w-14 h-14 rounded-xl overflow-hidden shadow-lg flex-shrink-0 border-2 border-primary/20 group-hover:border-primary/60 transition-colors">
                <img
                  src="/images/college_logo.jpeg"
                  alt="شعار كلية بوابة التكنولوجيا"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-base font-bold text-foreground leading-tight">كلية بوابة التكنولوجيا</span>
                <span className="text-xs text-muted-foreground">Technology Gateway College</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center gap-0.5">
              {navigationItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'text-foreground hover:bg-muted hover:text-primary'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}

              {/* Portals Dropdown */}
              <div ref={portalsRef} className="relative">
                <button
                  onClick={() => setPortalsOpen(!portalsOpen)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1 ${
                    portalsOpen
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'text-foreground hover:bg-muted hover:text-primary'
                  }`}
                >
                  البوابات الإلكترونية
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${portalsOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {portalsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.97 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-2 w-72 bg-card rounded-2xl shadow-2xl border border-border overflow-hidden z-50"
                    >
                      <div className="p-2">
                        {portalLinks.map((portal) => (
                          <a
                            key={portal.url}
                            href={portal.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setPortalsOpen(false)}
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors group"
                          >
                            <div className={`w-10 h-10 rounded-xl ${portal.bg} flex items-center justify-center flex-shrink-0`}>
                              <portal.icon className={`w-5 h-5 ${portal.color}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-1">
                                <p className="font-semibold text-sm">{portal.label}</p>
                                <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <p className="text-xs text-muted-foreground">{portal.desc}</p>
                            </div>
                          </a>
                        ))}
                      </div>
                      <div className="border-t border-border bg-muted/50 px-4 py-2">
                        <p className="text-xs text-muted-foreground text-center">
                          تحتاج اسم مستخدم وكلمة مرور للدخول
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="xl:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Overlay behind mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[99] xl:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Menu - fixed position so always visible on scroll */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
              initial={{ opacity: 0, x: 300 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 300 }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="fixed top-0 right-0 h-full w-72 bg-card border-l border-border overflow-y-auto z-[100] shadow-2xl"
            >
              <div className="flex items-center justify-between p-4 border-b border-border">
                <span className="font-bold text-foreground">القائمة</span>
                <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-muted">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="p-4 flex flex-col gap-1">
                {navigationItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className="px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 text-right text-foreground hover:bg-primary hover:text-primary-foreground"
                  >
                    {item.label}
                  </button>
                ))}
                <div className="border-t border-border mt-2 pt-2">
                  <p className="px-4 py-2 text-xs font-bold text-muted-foreground uppercase tracking-wider">البوابات الإلكترونية</p>
                  {portalLinks.map((portal) => (
                    <a
                      key={portal.url}
                      href={portal.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-muted transition-colors"
                    >
                      <div className={`w-8 h-8 rounded-lg ${portal.bg} flex items-center justify-center`}>
                        <portal.icon className={`w-4 h-4 ${portal.color}`} />
                      </div>
                      <span className="font-medium text-sm">{portal.label}</span>
                      <ExternalLink className="w-3 h-3 text-muted-foreground mr-auto" />
                    </a>
                  ))}
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>

      <main className="flex-1 pt-20">{children}</main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground mt-auto">
        <div className="container mx-auto px-4 py-12">
          {/* Footer Logo */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-10 pb-8 border-b border-primary-foreground/20">
            <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-primary-foreground/30 flex-shrink-0">
              <img src="/images/college_logo.jpeg" alt="شعار الكلية" className="w-full h-full object-cover" />
            </div>
            <div>
              <h2 className="text-xl font-bold">كلية بوابة التكنولوجيا</h2>
              <p className="text-sm text-primary-foreground/70">Technology Gateway College</p>
              <p className="text-sm text-primary-foreground/70 mt-1">معتمدة من الهيئة الليبية لضمان الجودة</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-bold mb-4 text-accent">روابط سريعة</h3>
              <ul className="space-y-2">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="text-sm hover:text-accent transition-colors inline-flex items-center gap-1 opacity-80 hover:opacity-100"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4 text-accent">الخدمات الإلكترونية</h3>
              <ul className="space-y-2">
                {academicServices.map((service) => (
                  <li key={service.label}>
                    <a
                      href={service.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm hover:text-accent transition-colors inline-flex items-center gap-1 opacity-80 hover:opacity-100"
                    >
                      {service.label}
                      {service.url !== '#' && <ExternalLink className="w-3 h-3" />}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4 text-accent">معلومات الاتصال</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <div className="flex flex-col gap-1 opacity-80">
                    <a href="tel:0917430505" className="hover:text-accent transition-colors">0917430505</a>
                    <a href="tel:0927430505" className="hover:text-accent transition-colors">0927430505</a>
                    <a href="tel:0947430505" className="hover:text-accent transition-colors">0947430505</a>
                  </div>
                </li>
                <li className="flex items-center gap-2 text-sm opacity-80">
                  <Mail className="w-5 h-5 flex-shrink-0" />
                  <a href="mailto:info@cgot.edu.ly" className="hover:text-accent transition-colors">
                    info@cgot.edu.ly
                  </a>
                </li>
                <li className="flex items-start gap-2 text-sm opacity-80">
                  <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>الأحد - الخميس: 8:00 ص - 4:00 م</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-primary-foreground/20 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-center md:text-right opacity-70">
              © 2026 كلية بوابة التكنولوجيا. جميع الحقوق محفوظة.
            </p>
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/CollegGate" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors opacity-70 hover:opacity-100">
                <SiFacebook className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/cgotcollege/" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors opacity-70 hover:opacity-100">
                <SiInstagram className="w-5 h-5" />
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors opacity-70 hover:opacity-100">
                <SiX className="w-5 h-5" />
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors opacity-70 hover:opacity-100">
                <SiLinkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
